import { Box, Typography, images } from "@analyticsMD/quil";
import { css } from "@emotion/css";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { common, deepPurple, grey } from "@mui/material/colors";
import { alpha } from "@mui/material/styles";
import { useContext } from "react";

import MalnutritionAPIContext from "../../contexts/MalnutritionAPIContext";
import { getPatientIdentifier } from "../../utils/patient-identifier";

import type { FC, ReactNode } from "react";
import type {
  MalnutritionPatientSummary,
  MalnutritionStatus,
} from "../../types/Malnutrition";

const styles = {
  wrapper: css`
    background: url("${images.PatientBackground}") ${deepPurple[100]};
    background-size: cover;
    color: ${common.white};
    padding: 1rem 1rem 1.25rem 1rem;
    box-sizing: border-box;
  `,
  topRow: css`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 0.5rem;
  `,
  iconButton: css`
    color: ${common.white};
    background-color: ${alpha(common.white, 0.15)};
    width: 1.75rem;
    height: 1.75rem;
  `,
};

const sxStyles = {
  name: {
    fontSize: "1.625rem",
    fontWeight: "bold",
    lineHeight: 1.2,
    wordBreak: "break-word",
    flex: 1,
    minWidth: 0,
  },
  demographics: {
    fontSize: "0.75rem",
    opacity: 0.95,
  },
  bottomRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "0.5rem",
    marginTop: "0.5rem",
  },
  statusChip: {
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    boxSizing: "border-box",
    padding: "0.125rem 0.625rem",
    borderRadius: "999px",
    // Opaque white on dark neutral text, per the design. Not translucent: the
    // header's background image is textured and shows through any transparency.
    // No border — an outline on an opaque white fill has nothing to separate.
    backgroundColor: common.white,
    color: grey[900],
    fontSize: "0.75rem",
    fontWeight: 500,
    lineHeight: 1.5,
    whiteSpace: "nowrap",
  },
  iconButtonRoot: {
    color: common.white,
    backgroundColor: alpha(common.white, 0.15),
    width: "1.75rem",
    height: "1.75rem",
    padding: "0.25rem",
    "&:hover": { backgroundColor: alpha(common.white, 0.25) },
  },
};

// The risk chip lives in the header rather than the row below (ENG-30844): on
// the card the status reads by word on a plain white outline, leaving colour
// free to mean clinical decline. The list view still colours by status.
const STATUS_LABEL: Record<MalnutritionStatus, string> = {
  high_risk: "High Risk",
  medium_risk: "Medium Risk",
  protocol: "Protocol",
  following: "Following",
  consult: "Consult",
};

interface MalnutritionHeaderProps {
  patient: MalnutritionPatientSummary;
  // Null when the patient isn't flagged (or the status can't be mapped) —
  // the header then carries no chip at all.
  status?: MalnutritionStatus | null;
  onExpand?: () => void;
  onClose?: () => void;
  /** Override the close affordance when the host provides one (e.g., drawer chrome). */
  closeButtonCmpt?: ReactNode;
}

const MalnutritionHeader: FC<MalnutritionHeaderProps> = ({
  patient,
  status = null,
  onExpand,
  onClose,
  closeButtonCmpt,
}) => {
  const { configurationQuery } = useContext(MalnutritionAPIContext);
  // Build from non-empty segments so coalesced-empty fields (age/gender/bed/
  // unit/identifier) don't leave dangling separators like "Bed 412A ·  · 4 East".
  const genderInitial = patient.gender
    ? patient.gender.charAt(0).toUpperCase()
    : "";
  const ageGender = `${patient.age ?? ""}${genderInitial}`;
  const demographics = [
    patient.current_bed ? `Bed ${patient.current_bed}` : "",
    ageGender,
    patient.current_unit,
    getPatientIdentifier(patient, configurationQuery?.data?.[0]),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Box className={styles.wrapper}>
      <Box className={styles.topRow}>
        <Typography sx={sxStyles.name}>{patient.full_name}</Typography>
        <Box sx={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          {onExpand && (
            <IconButton
              size="small"
              onClick={onExpand}
              aria-label="Expand"
              sx={sxStyles.iconButtonRoot}
            >
              <OpenInFullIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          )}
          {closeButtonCmpt ??
            (onClose && (
              <IconButton
                size="small"
                onClick={onClose}
                aria-label="Close"
                sx={sxStyles.iconButtonRoot}
              >
                <CloseIcon sx={{ fontSize: "1.1rem" }} />
              </IconButton>
            ))}
        </Box>
      </Box>
      <Box sx={sxStyles.bottomRow}>
        <Typography sx={sxStyles.demographics}>{demographics}</Typography>
        {status && (
          <Box
            role="status"
            aria-label={`Malnutrition status: ${STATUS_LABEL[status]}`}
            sx={sxStyles.statusChip}
          >
            {STATUS_LABEL[status]}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MalnutritionHeader;
